/*
* Copyright © 2019 Insticator, Inc. All Rights Reserved.
*
* CONFIDENTIALITY NOTICE: THIS SOFTWARE CONTAINS CONFIDENTIAL INFORMATION OF
* INSTICATOR, INC. USE, DISCLOSURE OR REPRODUCTION IS PROHIBITED WITHOUT THE PRIOR
* EXPRESS WRITTEN PERMISSION OF INSTICATOR, INC.
*/

/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
import Component from 'inferno-component';
import Structure from 'components/shared/structure/structure_container';
// unique elements
import Frame from 'components/frames/frame_container';
import QuestionSet from 'components/content/shared/questionset/questionset';
import { preloadDataImages } from "utils/preloader";
import { trackEvent } from 'utils/tracking/tracking';
import { log } from 'utils/logger';
// STYLES
// (css-in-js, imported as JS from external file, turned into CSS via library used in utils)
// import styles from 'components/content/quiz/quiz_styles';
// import { createStyles } from 'utils/styles';
// const sheet = createStyles(styles, {link:false});



/* ========== ~~~~~~~~~~ QUESTIONNAIRE : CHILD ~~~~~~~~~~ ========== */
// PRESENTATIONAL COMPONENT
// receives app state & actions as props from parent "_container" component
// this child component handles local state and uses props passed from parent to read/write application state when necessary
class QuestionnaireContent extends Component {
    constructor(props) {
        super(props);
        this.currentQuestionnaire = null;
        this.currentQuestionnaireId = null;
        this.currentQuestionnaireType = null;
        this.currentNumOfQuestions = 0;
        this.currentQuestionnaireBGImage = null;
        this.questionnairesCompleted = 0;
        this.dataRequested = false;
        this.lastActivatedOnFrame = 'none';
    }

    componentWillMount() {
        this.setInitialFrameStatus();
    }

    componentWillReceiveProps(nextProps) {
        // FETCH CONTENT DATA
        if ((this.isContentActive() || this.isContentActive(nextProps)) && (this.props.dataFetchingPermitted || nextProps.dataFetchingPermitted)) {
            const cookieId = !!this.props.userId ? this.props.userId : nextProps.userId;
            this.fetchData(cookieId);
        }

        // Reset variable used to prevent multiple fetches on content deactivation. This will allow the content to re-fetch the data when the queue repeats.
        if (this.isContentActive() && !this.isContentActive(nextProps)) {
            this.resetClassVariables()
        }

        if (this.props.currentContentStatus['status'] === 'active' && nextProps.currentContentStatus['status'] !== 'active' && this.props.currentContentData.ready && this.props.currentContentData.data.length !== 0) {
            this.processQuestionnaireCompletion(nextProps)
        }

        if (this.isActiveContentWithFrames() && this.isActiveContentWithFrames(nextProps) && this.props.currentContentStatus.frame !== nextProps.currentContentStatus.frame) {
            const nextFrame = nextProps.currentContentStatus.frame;
            if(nextProps.questionnaires !== null && nextProps.questionnaires[nextFrame] !== undefined) this.activateQuestionnaire(nextFrame, nextProps);
        }

        if (!this.isActiveContentWithFrames() && this.isActiveContentWithFrames(nextProps)) { // component will reactivate
            this.setInitialFrameStatus();
        } else if (this.props.currentContentStatus['status'] === 'active' && !this.props.currentContentData.ready && nextProps.currentContentData.ready) {
            nextProps.currentContentData.data.length === 0 ? this.removeContent() : this.setInitialFrameStatus(nextProps);
        } else if (!this.props.currentContentData.ready && nextProps.currentContentData.ready) {
            nextProps.currentContentData.data.length !== 0 ? this.setInitialFrameStatus(nextProps) : this.removeContent();
        } else if (this.props.currentContentStatus['status'] !== 'active' && nextProps.currentContentStatus['status'] === 'active' && this.props.currentContentData.ready) {
            this.removeContent();
        }
    }

    _formCallParameters() {
        const pqSettings = this.props.currentContentSettings.uniqueProps;
        const isLifestyle = pqSettings.questionnaireType === 'LIFESTYLE';
        return {
            isLifestyle,
            surveyType: pqSettings.questionnaireType,
            qc: pqSettings.numberOfQuestions, // Question Count
            page_url: isLifestyle ? encodeURIComponent(window.parent.document.location.href) : null, // document.location.ancestorOrigins[0]
            sc: isLifestyle ? pqSettings.numberOfQuestionnaires : null,// Survey Count
            mqc: isLifestyle && pqSettings.minimumQuestions !== undefined ? pqSettings.minimumQuestions : null, // Minimum Question Count
            exclude: isLifestyle ? this.props.excludedQuestionnaires : null, // Exclude Categories
        };
    }

    fetchData(cookieId) {
        if (this.dataRequested) { return null; }
        log('Fetching with cookieid', cookieId)
        this.dataRequested = true;
        this.props.fetchQuestionnaireData(this.props.contentId, cookieId, this._formCallParameters(), this.props.isPubPortalPreview)
    }

    removeContent() {
        this.props.deactivateContents([this.props.contentId]);
    }

    processQuestionnaireCompletion() {
        this.activateQuestionnaire(null);
    }

    activateQuestionnaire(frame, props=this.props) {
        // prevent from activating same questionnaire
        if(frame === this.lastActivatedOnFrame) return null;
        this.lastActivatedOnFrame = frame;

        if(frame !== 0) this.trackQuestionnaire();
        if(frame !== 0) this.props.excludeQuestionnaire(this.currentQuestionnaire);

        this.currentQuestionnaire = frame !== null ? props.questionnaires[frame]['category'] : null;
        this.currentQuestionnaireId = frame !== null ? props.questionnaires[frame]['id'] : null;
        this.currentQuestionnaireType = frame !== null ? props.questionnaires[frame]['type'] : null;
        this.currentQuestionnaireBGImage = frame !== null ? props.questionnaires[frame]['backgroundImageURL'] : null;
        this.currentNumOfQuestions = frame !== null ? props.questionnaires[frame]['numOfQuestions'] : 0;


        const num = this.props.currentContentStatus['totalFrameCount'] - this.props.currentContentStatus['frame'];
        if (this.targetCompleteReached()) this.props.advanceMultipleFrames(this.props.contentId, num);
    }

    targetCompleteReached() {
        return this.props.targetCompletion !== null && this.questionnairesCompleted >= this.props.targetCompletion;
    }

    trackQuestionnaire() {
        trackEvent({
          type: 'load',
          name: 'ceuContentEngaged',
          target: 'questionnaireComplete',
          props: this.props,
          additionalProps: {
            id: this.currentQuestionnaireId,
            category: this.currentQuestionnaire,
            type: this.currentQuestionnaireType,
            numOfQuestions: this.currentNumOfQuestions,
            numOfPreviouslyTakenQuestionnaires: this.questionnairesCompleted
          }
        });
        this.questionnairesCompleted +=  1;
    }

    setInitialFrameStatus(props=this.props) {
        if(props.questionnaires !== null && props.questionnaires[0] !== undefined) this.activateQuestionnaire(0, props);
    }

    createFrameAndAnswerStatus(props) {
        let frameStatus = {};
        let chosenAnswers = {};
        for (let idx = 0; idx < props.currentContentData['data'].length; idx++) {
            frameStatus[idx] = 'unanswered';
            chosenAnswers[idx] = null;
        }
        return { frameStatus, chosenAnswers };
    }

    createFrames() {
        // loop the returned data and create a frame component based off of it
        // store the frame in an array to be returned on render
        // create markup for questions and answers and pass that data to the frame component for display purposes (handled below)
        const contentId = this.props.contentId;
        const contentType = this.props.currentContentSettings.type;
        const showFrameForXSeconds = this.props.currentContentSettings.showFrameForXSeconds;
        const showResultForXSeconds = this.props.currentContentSettings.uniqueProps.showResultForXSeconds;
        const frameStatus = this.props.currentContentStatus.uniqueState.frameStatus;
        const hasRewardsComponent = this.props.rewardsEnabled && this.props.rewardsPermitted;
        const advanceFrame = () => this.props.advanceFrame(this.props.contentId);
        const frames = [];
        this.props.currentContentData['data'].forEach((frameData, idx) => {
            switch (frameData.type) {
                case 'quiz':
                case 'poll':
                default:
                    let questionSetData = {
                        id: `${contentId}-${idx}`,
                        frameData: frameData,
                        frame: idx, contentId,
                        showFrameForXSeconds,
                        showResultForXSeconds,
                        frameStatus,
                        canSkipFrames: this.props.currentContentSettings.canSkipFrames,
                        advanceFrame,
                        contentType,
                        hasRewardsComponent,
                        currentQuestionnaire: {
                            id: this.currentQuestionnaireId,
                            type: this.currentQuestionnaireType,
                            category: this.currentQuestionnaire,
                            numOfQuestions: this.currentNumOfQuestions
                        }
                    };
                    frames.push(<Frame id={frameData.question.id} type={frameData.question.type} frameIndex={idx} contentId={this.props.contentId} markup={QuestionSet(questionSetData)} />);
            }
        });
        return frames;
    }

    isContentActive(props = this.props) {
        return props.currentContentStatus['status'] === 'active'
    }

    isActiveContentWithFrames(props = this.props) {
        return this.isContentActive(props) && !this.isOutOfFrames();
    }

    isOutOfFrames(props=this.props) {
        if(props.currentContentStatus['totalFrameCount'] === null) { return false; }
        return props.currentContentStatus['frame'] >= props.currentContentStatus['totalFrameCount'];
    }

    resetClassVariables = () => {
        this.currentQuestionnaire = null;
        this.currentQuestionnaireId = null;
        this.currentQuestionnaireType = null;
        this.currentNumOfQuestions = 0;
        this.currentQuestionnaireBGImage = null;
        this.questionnairesCompleted = 0;
        this.dataRequested = false;
        this.lastActivatedOnFrame = 'none';
    };

    render() {
        if (!this.isActiveContentWithFrames()) return null;
        return(
            <div className={`uniquecontent ${this.props.currentContentStatus['type']}`}>
                <Structure contentId={this.props.contentId} markup={this.createFrames()} />
            </div>
        );
    }
}



/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default QuestionnaireContent;
